/*
 * seo/head-manager.ts — the framework-agnostic core of the per-route head layer.
 *
 * `applyPageMeta(document, options)` synchronously updates the document title,
 * description, canonical link, robots directive, Open Graph / Twitter tags and an
 * (optional) JSON-LD block, then returns an `undo` that restores the previous
 * state exactly. It is a pure function of `(document, options)` — no React, no
 * globals — so it is trivially testable and safe under React StrictMode's
 * mount → cleanup → mount double-invocation (each apply captures the baseline it
 * later restores).
 *
 * Tags that already exist in the static base (`index.html`) are mutated in place
 * and their prior value is restored on undo; tags we create are removed on undo.
 * A missing DOM (SSR) is not this module's concern — the hook guards for it.
 */
import { DEFAULT_IMAGE, absoluteUrl } from './config';
import type { JsonLd, PageMetaOptions } from './types';

/** Marks meta/link elements created by this module (so undo can remove them). */
const MANAGED_ATTR = 'data-managed-seo';
/** Marks the JSON-LD script we manage (so it can be swapped/removed cleanly). */
const JSONLD_ATTR = 'data-managed-jsonld';

/** A reversible mutation: calling it restores the pre-apply state. */
type Undo = () => void;

interface UpsertSpec {
  /** Selector locating an existing element in `<head>`. */
  selector: string;
  /** Build the element (with its identifying attribute) when none exists. */
  create: (doc: Document) => HTMLElement;
  /** The value-bearing attribute to write ("content" or "href"). */
  attr: string;
  /** The value to write. */
  value: string;
}

/**
 * Set `attr=value` on the element matched by `selector`, creating it in `<head>`
 * when absent. Returns an undo that restores the previous attribute value (or
 * removes the element we created).
 */
function upsert(doc: Document, spec: UpsertSpec): Undo {
  const existing = doc.head.querySelector<HTMLElement>(spec.selector);
  if (existing) {
    const prev = existing.getAttribute(spec.attr);
    existing.setAttribute(spec.attr, spec.value);
    return () => {
      if (prev === null) existing.removeAttribute(spec.attr);
      else existing.setAttribute(spec.attr, prev);
    };
  }
  const created = spec.create(doc);
  created.setAttribute(spec.attr, spec.value);
  created.setAttribute(MANAGED_ATTR, 'true');
  doc.head.appendChild(created);
  return () => created.remove();
}

/** Upsert a `<meta name="…" content="…">`. */
function metaByName(doc: Document, name: string, value: string): Undo {
  return upsert(doc, {
    selector: `meta[name="${name}"]`,
    create: (d) => {
      const el = d.createElement('meta');
      el.setAttribute('name', name);
      return el;
    },
    attr: 'content',
    value,
  });
}

/** Upsert a `<meta property="…" content="…">` (Open Graph). */
function metaByProperty(doc: Document, property: string, value: string): Undo {
  return upsert(doc, {
    selector: `meta[property="${property}"]`,
    create: (d) => {
      const el = d.createElement('meta');
      el.setAttribute('property', property);
      return el;
    },
    attr: 'content',
    value,
  });
}

/** Upsert a `<link rel="…" href="…">`. */
function linkByRel(doc: Document, rel: string, value: string): Undo {
  return upsert(doc, {
    selector: `link[rel="${rel}"]`,
    create: (d) => {
      const el = d.createElement('link');
      el.setAttribute('rel', rel);
      return el;
    },
    attr: 'href',
    value,
  });
}

/**
 * Replace any managed JSON-LD with a fresh block for `jsonLd`. Returns an undo
 * that removes the injected script. No-op (empty undo) when `jsonLd` is absent.
 */
function applyJsonLd(doc: Document, jsonLd: JsonLd | JsonLd[] | undefined): Undo {
  if (!jsonLd) return () => {};
  // Defensively clear any stale managed block before adding ours.
  doc.head.querySelectorAll(`script[${JSONLD_ATTR}]`).forEach((node) => node.remove());
  const script = doc.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(JSONLD_ATTR, 'true');
  script.textContent = JSON.stringify(jsonLd);
  doc.head.appendChild(script);
  return () => script.remove();
}

/**
 * Apply all per-route head tags for `options` and return an `undo` that restores
 * the exact prior state (title, attributes, injected nodes) in reverse order.
 */
export function applyPageMeta(doc: Document, options: PageMetaOptions): Undo {
  const {
    title,
    description,
    canonicalPath,
    ogType = 'website',
    image,
    noindex = false,
    jsonLd,
  } = options;

  const url = absoluteUrl(canonicalPath);
  const imageUrl = image ? absoluteUrl(image) : DEFAULT_IMAGE;
  const robots = noindex ? 'noindex,nofollow' : 'index,follow';

  const undos: Undo[] = [];

  // Title layer is optional: without it the current title (owned elsewhere, e.g.
  // by the page's own effect) is left untouched.
  if (title !== undefined) {
    const prevTitle = doc.title;
    doc.title = title;
    undos.push(() => {
      doc.title = prevTitle;
    });
  }

  undos.push(metaByName(doc, 'description', description));
  undos.push(metaByName(doc, 'robots', robots));
  undos.push(linkByRel(doc, 'canonical', url));

  if (title !== undefined) undos.push(metaByProperty(doc, 'og:title', title));
  undos.push(metaByProperty(doc, 'og:description', description));
  undos.push(metaByProperty(doc, 'og:url', url));
  undos.push(metaByProperty(doc, 'og:type', ogType));
  undos.push(metaByProperty(doc, 'og:image', imageUrl));

  // `summary_large_image`, não `summary`: a imagem padrão é o card 1200×630 de
  // `public/og.png`, e um card pequeno a recortaria num quadrado central.
  undos.push(metaByName(doc, 'twitter:card', 'summary_large_image'));
  if (title !== undefined) undos.push(metaByName(doc, 'twitter:title', title));
  undos.push(metaByName(doc, 'twitter:description', description));
  undos.push(metaByName(doc, 'twitter:image', imageUrl));

  undos.push(applyJsonLd(doc, jsonLd));

  return () => {
    for (let i = undos.length - 1; i >= 0; i -= 1) undos[i]!();
  };
}
